import i18n from "i18next";
import type { ResourceLanguage } from "i18next";
import { initReactI18next } from "react-i18next";
import { useState, useLayoutEffect, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import translationDe from "locales/de/translation.json";
import translationEn from "locales/en/translation.json";
import {
  CommerceEnvironment,
  type LanguageMapShortValueType,
} from "constants/common";

const DEFAULT_NAMESPACE = "translation";
const RESOURCES: Record<LanguageMapShortValueType, ResourceLanguage> = {
  de: {
    [DEFAULT_NAMESPACE]: translationDe,
  },
  en: {
    [DEFAULT_NAMESPACE]: translationEn,
  },
};

i18n.use(initReactI18next).init({
  load: "languageOnly",
  lng: CommerceEnvironment.defaultLang,
  fallbackLng: CommerceEnvironment.defaultLang,
  ns: [DEFAULT_NAMESPACE],
  defaultNS: DEFAULT_NAMESPACE,
  resources: RESOURCES,
  debug: false,
  interpolation: {
    /* 
        This setting is needed so that translation parameters
        (second param of the t function) do not get escaped
        Otherwise for example "&" would be escaped to "&amp;" 
    */
    escapeValue: false,
  },
});

if (typeof document !== "undefined") {
  // Set the `lang` attribute of the <html> tag when loading
  document.documentElement.setAttribute("lang", i18n.language);
}

// Update the `lang` attribute of the <html> tag when changing the language
i18n.on("languageChanged", (lng) => {
  document.documentElement.setAttribute("lang", lng);
});

/**
 * Hook to retrieve the current language.
 */
export const useCurrentLanguage = () => {
  const [currentLanguage, setCurrentLanguage] = useState<string>(
    i18n.language || CommerceEnvironment.defaultLang
  );

  const handleLanguageChange = useCallback((lng) => {
    dayjs.locale(lng);
    setCurrentLanguage(lng || CommerceEnvironment.defaultLang);
  }, []);

  useLayoutEffect(() => {
    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [handleLanguageChange]);

  const isGerman = useMemo(
    () => currentLanguage.startsWith(CommerceEnvironment.LANG_DE),
    [currentLanguage]
  );

  return { currentLanguage, isGerman };
};

export default i18n;
